import { useState, useRef } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { useCreateComment } from '@/hooks/useComments'
import { useProjectMembers } from '@/hooks/useProjectMembers'
import type { CommentableType } from '@/types/database'
import { Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAnalytics } from '@/hooks/useAnalytics'

interface CommentFormProps {
  projectId: string
  commentableType: CommentableType
  commentableId: string
  onCommentCreated?: () => void
}

interface MentionSuggestion {
  id: string
  name: string
  email: string
}

export function CommentForm({
  projectId,
  commentableType,
  commentableId,
  onCommentCreated,
}: CommentFormProps) {
  const [content, setContent] = useState('')
  const [showMentions, setShowMentions] = useState(false)
  const [mentionSearch, setMentionSearch] = useState('')
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 })
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const mentionListRef = useRef<HTMLDivElement>(null)

  const createComment = useCreateComment()
  const { data: projectMembers = [] } = useProjectMembers(projectId)
  const { trackCommentCreated } = useAnalytics()

  // Filter members based on search
  const filteredMembers = projectMembers.filter((member) => {
    const searchLower = mentionSearch.toLowerCase()
    const name = member.profile?.full_name || member.profile?.email || ''
    const email = member.profile?.email || ''
    return (
      name.toLowerCase().includes(searchLower) ||
      email.toLowerCase().includes(searchLower)
    )
  })

  // Handle textarea changes and detect @mention trigger
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value
    setContent(newContent)

    const cursorPos = e.target.selectionStart
    const textBeforeCursor = newContent.slice(0, cursorPos)

    // Check if we're typing an @mention
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/)

    if (mentionMatch) {
      setMentionSearch(mentionMatch[1])
      setShowMentions(true)
      setSelectedMentionIndex(0)

      // Calculate position for mention dropdown
      const textarea = textareaRef.current
      if (textarea) {
        const { top, left } = getCaretCoordinates(textarea, cursorPos)
        setMentionPosition({ top, left })
      }
    } else {
      setShowMentions(false)
    }
  }

  // Get caret coordinates for positioning mention dropdown
  const getCaretCoordinates = (element: HTMLTextAreaElement, position: number) => {
    const div = document.createElement('div')
    const style = getComputedStyle(element)

    // Copy textarea styles to div
    ;[
      'fontFamily',
      'fontSize',
      'fontWeight',
      'wordWrap',
      'whiteSpace',
      'borderLeftWidth',
      'borderTopWidth',
      'borderRightWidth',
      'borderBottomWidth',
      'paddingLeft',
      'paddingTop',
      'paddingRight',
      'paddingBottom',
    ].forEach((prop) => {
      div.style[prop as any] = style[prop as any]
    })

    div.style.position = 'absolute'
    div.style.visibility = 'hidden'
    div.style.width = `${element.clientWidth}px`
    div.style.height = `${element.clientHeight}px`

    const textContent = element.value.substring(0, position)
    div.textContent = textContent

    const span = document.createElement('span')
    span.textContent = element.value.substring(position) || '.'
    div.appendChild(span)

    document.body.appendChild(div)
    const { offsetTop: top, offsetLeft: left } = span
    document.body.removeChild(div)

    return { top: top + 20, left }
  }

  // Insert mention into textarea
  const insertMention = (member: MentionSuggestion) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const cursorPos = textarea.selectionStart
    const textBeforeCursor = content.slice(0, cursorPos)
    const textAfterCursor = content.slice(cursorPos)

    // Remove the @ and partial name
    const beforeMention = textBeforeCursor.replace(/@\w*$/, '')
    const mention = `@[${member.name}] `
    const newContent = beforeMention + mention + textAfterCursor

    setContent(newContent)
    setShowMentions(false)
    setMentionSearch('')

    // Set cursor position after mention
    setTimeout(() => {
      const newPos = beforeMention.length + mention.length
      textarea.setSelectionRange(newPos, newPos)
      textarea.focus()
    }, 0)
  }

  // Handle keyboard navigation in mention list
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentions) {
      // Submit on Cmd/Ctrl + Enter
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      }
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedMentionIndex((prev) =>
        prev < filteredMembers.length - 1 ? prev + 1 : prev
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedMentionIndex((prev) => (prev > 0 ? prev - 1 : 0))
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (filteredMembers.length > 0) {
        e.preventDefault()
        const selectedMember = filteredMembers[selectedMentionIndex]
        insertMention({
          id: selectedMember.user_id,
          name: selectedMember.profile?.full_name || selectedMember.profile?.email || 'Unknown',
          email: selectedMember.profile?.email || '',
        })
      }
    } else if (e.key === 'Escape') {
      setShowMentions(false)
    }
  }

  // Extract mentioned user IDs from content
  const extractMentionedUserIds = (): string[] => {
    const mentionRegex = /@\[([^\]]+)\]/g
    const mentionedNames: string[] = []
    let match

    while ((match = mentionRegex.exec(content)) !== null) {
      mentionedNames.push(match[1])
    }

    // Map names back to user IDs
    return projectMembers
      .filter((member) => {
        const name = member.profile?.full_name || member.profile?.email || ''
        return mentionedNames.includes(name)
      })
      .map((member) => member.user_id)
  }

  const handleSubmit = async () => {
    if (!content.trim()) return

    const mentionedUserIds = extractMentionedUserIds()

    try {
      await createComment.mutateAsync({
        content: content.trim(),
        project_id: projectId,
        commentable_type: commentableType,
        commentable_id: commentableId,
        mentioned_user_ids: mentionedUserIds,
      })

      // Track comment creation
      trackCommentCreated({
        project_id: projectId,
        commentable_type: commentableType,
        commentable_id: commentableId,
        has_mentions: mentionedUserIds.length > 0,
        mention_count: mentionedUserIds.length,
      })

      setContent('')
      onCommentCreated?.()
    } catch (error) {
      console.error('[CommentForm] Error creating comment:', error)
    }
  }

  const characterLimit = 5000
  const remaining = characterLimit - content.length

  return (
    <div className="relative">
      <div className="space-y-2">
        <Textarea
          ref={textareaRef}
          value={content}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          placeholder="Write a comment... Use @ to mention team members"
          className="min-h-[100px] resize-y"
          maxLength={characterLimit}
        />

        {/* Mention autocomplete dropdown */}
        {showMentions && filteredMembers.length > 0 && (
          <div
            ref={mentionListRef}
            className="absolute z-50 bg-popover border border-border rounded-md shadow-lg max-h-[200px] overflow-y-auto"
            style={{
              top: mentionPosition.top,
              left: mentionPosition.left,
              minWidth: '200px',
            }}
          >
            {filteredMembers.map((member, index) => (
              <button
                key={member.user_id}
                className={cn(
                  'w-full px-3 py-2 text-left text-sm hover:bg-accent transition-colors',
                  index === selectedMentionIndex && 'bg-accent'
                )}
                onClick={() =>
                  insertMention({
                    id: member.user_id,
                    name: member.profile?.full_name || member.profile?.email || 'Unknown',
                    email: member.profile?.email || '',
                  })
                }
                onMouseEnter={() => setSelectedMentionIndex(index)}
              >
                <div className="font-medium">
                  {member.profile?.full_name || member.profile?.email || 'Unknown User'}
                </div>
                {member.profile?.full_name && member.profile?.email && (
                  <div className="text-xs text-muted-foreground">{member.profile.email}</div>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <span
            className={cn(
              'text-xs',
              remaining < 100 ? 'text-destructive' : 'text-muted-foreground'
            )}
          >
            {remaining} characters remaining
          </span>

          <div className="flex gap-2">
            <Button
              onClick={handleSubmit}
              disabled={!content.trim() || createComment.isPending}
              size="sm"
            >
              <Send className="h-3 w-3 mr-1" />
              {createComment.isPending ? 'Posting...' : 'Post Comment'}
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Tip: Press Cmd/Ctrl + Enter to submit
        </p>
      </div>
    </div>
  )
}
